import {
  Badge, Button, DataTable, EmptyState, PageHeader, Row, useApi, useClampedPage, useI18n,
  type Column, type RegistrationGapResponse,
} from '@daghfal/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Children registered before an emergency contact and an allergy answer became required (Q9).
 *
 * Their records were left as they were rather than filled with a guess; this is the list of who still needs
 * either, and each name opens the child's screen, where both can be completed.
 */
export function RegistrationGaps() {
  const api = useApi();
  const { t } = useI18n();
  const [page, setPage] = useState(1);

  const gaps = useQuery({
    queryKey: ['registration-gaps', { page }],
    queryFn: () => api.children.registrationGaps({ page, pageSize: 50 }),
    placeholderData: keepPreviousData,
  });
  useClampedPage(page, setPage, gaps.data?.totalPages);

  const columns: Column<RegistrationGapResponse>[] = [
    {
      key: 'name',
      header: t('children.column.name'),
      render: (child) => (
        <Link className="dg-row-button" to={`/children/${child.id}`}>
          {`${child.preferredName ?? child.firstName} ${child.lastName}`}
        </Link>
      ),
    },
    {
      key: 'class',
      header: t('children.column.class'),
      render: (child) => child.className ?? <span className="dg-card__hint">{t('children.unassigned')}</span>,
    },
    {
      key: 'missing',
      header: t('registrationGaps.column.missing'),
      render: (child) => (
        <Row>
          {child.missingEmergencyContact && <Badge tone="warning">{t('registrationGaps.contact')}</Badge>}
          {child.missingAllergyAnswer && <Badge tone="warning">{t('registrationGaps.allergies')}</Badge>}
        </Row>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('registrationGaps.title')}
        lede={t('registrationGaps.lede')}
        actions={<Link className="dg-button dg-button--ghost" to="/children">{t('action.back')}</Link>}
      />

      <DataTable
        caption={t('registrationGaps.title')}
        columns={columns}
        rows={gaps.data?.items}
        rowKey={(child) => child.id}
        isLoading={gaps.isPending}
        error={gaps.error}
        onRetry={() => void gaps.refetch()}
        empty={<EmptyState icon="✅" title={t('registrationGaps.empty')} />}
      />

      {gaps.data && gaps.data.totalPages > 1 && (
        <Row between>
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            {t('action.previous')}
          </Button>
          <span>{page} / {gaps.data.totalPages}</span>
          <Button variant="secondary" disabled={page >= gaps.data.totalPages} onClick={() => setPage((p) => p + 1)}>
            {t('action.next')}
          </Button>
        </Row>
      )}
    </>
  );
}
